import { useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useAdminCategories, invalidateAdminCategories } from "../../hooks";
import { useAlert, useApi } from "../../hooks";
import { useLoaderStore } from "../../context";
import { API } from "../../utils/constants";
import { TextInput, NumberInput, ActiveToggle } from "../../components/Inputs";

// ── Category form modal ────────────────────────────────────────────────────

/**
 * CategoryModal — Create or edit a category.
 * @param {object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {object|null} props.category - If set, edit mode
 */
function CategoryModal({ open, onClose, category = null }) {
  const isEdit = !!category;
  const alert = useAlert();
  const { post, put } = useApi();
  const { setShowLoader } = useLoaderStore();

  const [name, setName] = useState(category?.name ?? "");
  const [displayOrder, setDisplayOrder] = useState(category?.displayOrder ?? 0);

  const validate = () => {
    if (!name.trim()) {
      alert("Validation", "Category name is required.", "warning");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setShowLoader(true);

    const payload = {
      name: name.trim(),
      displayOrder: Number(displayOrder) || 0,
    };

    try {
      if (isEdit) {
        await put(API.ADMIN.CATEGORIES.UPDATE(category.id), payload);
        alert("Updated", `"${name}" updated.`, "success");
      } else {
        await post(API.ADMIN.CATEGORIES.CREATE, payload);
        alert("Created", `"${name}" added as a category.`, "success");
      }
      await invalidateAdminCategories();
      onClose();
    } catch (err) {
      const detail = err?.data?.detail || "";
      if (err?.status === 409 || detail.includes("exists")) {
        alert("Conflict", "A category with that name already exists.", "error");
      } else {
        alert("Error", detail || "Failed to save category.", "error");
      }
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-charcoal/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full max-w-sm bg-white rounded-2xl border-2 border-charcoal shadow-[6px_6px_0_rgba(43,39,36,0.18)] p-6"
                data-testid="category-modal"
              >
                <div className="flex items-center justify-between mb-5">
                  <Dialog.Title className="font-display text-2xl text-charcoal uppercase">
                    {isEdit ? "EDIT CATEGORY" : "ADD CATEGORY"}
                  </Dialog.Title>
                  <button onClick={onClose} aria-label="Close modal" className="text-charcoal/40 hover:text-charcoal">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <TextInput
                    label="Name *"
                    name="category-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <NumberInput
                    label="Display Order"
                    name="category-display-order"
                    value={displayOrder}
                    onChange={(e) => setDisplayOrder(e.target.value)}
                  />
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleSubmit}
                    className="flex-1 bg-rust text-white font-heading tracking-widest py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
                    data-testid="category-submit-button"
                  >
                    {isEdit ? "SAVE" : "CREATE"}
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 border-2 border-teal text-teal font-heading tracking-wider py-2 rounded-xl hover:bg-teal hover:text-white transition-colors"
                  >
                    CANCEL
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// ── Delete confirmation ───────────────────────────────────────────────────

function DeleteCategoryDialog({ open, onClose, onConfirm, categoryName, itemCount }) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-charcoal/50" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className="w-full max-w-sm bg-white rounded-2xl border-2 border-charcoal shadow-[6px_6px_0_rgba(43,39,36,0.18)] p-6"
              data-testid="delete-category-dialog"
            >
              <Dialog.Title className="font-display text-2xl text-charcoal uppercase mb-3">
                DELETE CATEGORY
              </Dialog.Title>

              {itemCount > 0 ? (
                <p className="font-body text-rust text-sm mb-6">
                  Cannot delete <strong>"{categoryName}"</strong> — it has {itemCount} active item{itemCount !== 1 ? "s" : ""}. Deactivate or reassign all items first.
                </p>
              ) : (
                <p className="font-body text-charcoal/70 mb-6">
                  Delete <strong>"{categoryName}"</strong>? This is permanent and cannot be undone.
                </p>
              )}

              <div className="flex gap-3">
                {itemCount === 0 && (
                  <button
                    onClick={onConfirm}
                    className="flex-1 bg-rust text-white font-heading tracking-widest py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
                    data-testid="confirm-delete-category-button"
                  >
                    DELETE
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="flex-1 border-2 border-teal text-teal font-heading tracking-wider py-2 rounded-xl hover:bg-teal hover:text-white transition-colors"
                >
                  {itemCount > 0 ? "CLOSE" : "CANCEL"}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

// ── Categories page ───────────────────────────────────────────────────────

/**
 * Categories — Admin category management (CRUD).
 *
 * - List table with displayOrder and item count
 * - Create and Edit modals (shared CategoryModal)
 * - Delete with 409 guard: shows error message if category has active items
 */
export default function Categories() {
  const alert = useAlert();
  const { put, del } = useApi();
  const { setShowLoader } = useLoaderStore();
  const { categories, isLoading, error } = useAdminCategories();

  const [modalOpen, setModalOpen] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Remount key — guarantees blank-on-create / prefilled-on-edit / clear-on-success (FE-3).
  const [modalSession, setModalSession] = useState(0);

  const openCreate = () => { setEditCategory(null); setModalSession((n) => n + 1); setModalOpen(true); };
  const openEdit = (cat) => { setEditCategory(cat); setModalSession((n) => n + 1); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditCategory(null); };

  const handleToggleActive = async (cat) => {
    setShowLoader(true);
    try {
      await put(API.ADMIN.CATEGORIES.UPDATE(cat.id), { isActive: !cat.isActive });
      await invalidateAdminCategories();
      alert("Updated", `"${cat.name}" ${!cat.isActive ? "activated" : "deactivated"}.`, "success");
    } catch (err) {
      alert("Error", err?.data?.detail || "Failed to update category.", "error");
    } finally {
      setShowLoader(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleteTarget.itemCount > 0) return;
    setShowLoader(true);
    try {
      await del(API.ADMIN.CATEGORIES.DELETE(deleteTarget.id));
      await invalidateAdminCategories();
      alert("Deleted", `"${deleteTarget.name}" deleted.`, "success");
      setDeleteTarget(null);
    } catch (err) {
      if (err?.status === 409) {
        alert("Cannot delete", err?.data?.detail || "Category has active items.", "error");
      } else {
        alert("Error", err?.data?.detail || "Failed to delete category.", "error");
      }
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <main data-testid="admin-categories-page" className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl text-charcoal uppercase">Categories</h1>
          <p className="font-body text-charcoal/60 text-sm mt-1">
            Manage menu categories and display order.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="bg-rust text-white font-heading tracking-widest px-5 py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
          data-testid="add-category-button"
        >
          + ADD CATEGORY
        </button>
      </div>

      {isLoading && <p className="font-body text-charcoal/60">Loading categories…</p>}
      {error && <p className="font-body text-rust">Unable to load categories.</p>}

      {!isLoading && !error && (
        <div className="overflow-x-auto">
          <table className="w-full border-2 border-charcoal rounded-2xl overflow-hidden">
            <thead className="bg-charcoal text-white">
              <tr>
                {["Name", "Order", "Items", "Active", "Actions"].map((h) => (
                  <th key={h} className="font-heading tracking-wider text-left px-4 py-3 text-sm">
                    {h.toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categories.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-10 font-body text-charcoal/50">
                    No categories yet. Add one above.
                  </td>
                </tr>
              )}
              {[...categories]
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((cat) => (
                  <tr
                    key={cat.id}
                    className="border-b border-aluminum/20 hover:bg-cream/50 transition-colors"
                    data-testid={`category-row-${cat.id}`}
                  >
                    <td className="px-4 py-3 font-body text-charcoal">{cat.name}</td>
                    <td className="px-4 py-3 font-body text-charcoal/60 text-sm">{cat.displayOrder}</td>
                    <td className="px-4 py-3 font-body text-charcoal/60 text-sm" data-testid={`item-count-${cat.id}`}>
                      {cat.itemCount}
                    </td>
                    <td className="px-4 py-3">
                      <ActiveToggle
                        enabled={cat.isActive}
                        onToggle={() => handleToggleActive(cat)}
                        label={`Toggle ${cat.name} active`}
                        testId={`toggle-active-${cat.id}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEdit(cat)}
                          className="font-heading tracking-wider text-xs text-teal border-2 border-teal px-3 py-1 rounded-full hover:bg-teal hover:text-white transition-colors"
                          data-testid={`edit-category-${cat.id}`}
                        >
                          EDIT
                        </button>
                        <button
                          onClick={() => setDeleteTarget(cat)}
                          className="font-heading tracking-wider text-xs text-rust border-2 border-rust px-3 py-1 rounded-full hover:bg-rust hover:text-white transition-colors"
                          data-testid={`delete-category-${cat.id}`}
                        >
                          DELETE
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit modal — keyed so each open starts from fresh state */}
      <CategoryModal
        key={`${editCategory ? `edit-${editCategory.id}` : "create"}-${modalSession}`}
        open={modalOpen}
        onClose={closeModal}
        category={editCategory}
      />

      {/* Delete confirmation */}
      <DeleteCategoryDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        categoryName={deleteTarget?.name ?? ""}
        itemCount={deleteTarget?.itemCount ?? 0}
      />
    </main>
  );
}
